import type {CreateExtArgs, CreateExtensionArgument, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {STExtApi} from '../serviceTopology'
import type {SchemaAPI, SchemaExtensionAPI} from '../schema/schema'
import type {RMApi} from '../rendererModel'
import {isEmpty} from 'lodash'
import {type BuilderCompDetails, deriveFromManifest, type Schema} from './manifest'
import type {FetchFn} from '../../utils/fetchUtils'
import type {Builder, BuilderTypes, CompRef, Experiment, Pointer, SchemaFile} from '@wix/document-services-types'

export type {BuilderCompDetails} from './manifest'

import type {PlatformExtensionAPI} from '../platform'
import type {StructureExtensionAPI} from '../structure'
import type {InnerElementsExtensionAPI} from './innerElements'
import type {VariantsExtensionAPI} from '../variants/variants'
import {DATA_TYPES} from '../../constants/constants'
import {builderTypes} from '../../utils/builderUtils'
import {getComponentType} from '../../utils/dalUtils'

interface BuilderComponentsApi extends ExtensionAPI {
    setManifests(manifests: Record<string, Builder.ResolvedManifest>): void
    registerBuilderComponentsForApp(appData: any): Promise<void>
    deriveFromManifest(identifier: string, manifest: Builder.ResolvedManifest): BuilderCompDetails[]
    registerBuilderComponent(details: BuilderCompDetails): void
    registerBuilderComponentWithManifest(identifier: string, manifest: Builder.ResolvedManifest): void
    getArchType(componentType: string): string
    getDisplayName(componentType: string): string
    getData(compPointer: Pointer, withInnerElements: boolean, types?: string[]): OverrideOrDefaultData[]
    getStyle(compPointer: Pointer, withInnerElements: boolean, types?: string[]): OverrideOrDefaultData[]
    getBuilderTypes(compPointer: CompRef): BuilderTypes
    isBuilder(compType: string): boolean
}

interface BuilderComponentsExtensionAPI extends ExtensionAPI {
    builderComponents: BuilderComponentsApi
}

interface BuilderComponentsPublicExtensionAPI extends ExtensionAPI {
    builderComponents: {
        setManifests: BuilderComponentsApi['setManifests']
    }
}

interface ManifestMetaData {
    typeNameInManifest: 'dataType' | 'cssPropertyType'
    dataType: 'data' | 'style'
}

type ExtractDataFunction = (
    meta: Record<string, any>,
    override: any,
    prop: string
) => {value: any; whereToWriteInDM: string}

const MANIFEST_TYPE_TO_DATA_PROPS = {
    data: ['data'],
    style: ['cssProperties', 'cssCustomProperties']
}

const typeToExtractDataFunction: Record<string, ExtractDataFunction> = {
    data: (meta: Record<string, any>, override: any, prop: string) => {
        const defaultValue = meta?.defaultValue
        return {
            value: override?.data?.[prop] ?? defaultValue,
            whereToWriteInDM: 'data'
        }
    },
    style: (meta: Record<string, any>, override: any, prop: string) => {
        const customOrProp = meta?.cssCustomProperty ? 'cssCustomProperties' : 'cssProperties'
        const defaultValue = meta?.defaultValue
        return {
            value: override?.[customOrProp]?.[prop] ?? defaultValue,
            whereToWriteInDM: customOrProp
        }
    }
}

const getComponentDataValues = (dataItem: any, manifestDataType: ManifestMetaData['dataType']) => {
    return MANIFEST_TYPE_TO_DATA_PROPS[manifestDataType].reduce((override: any, prop: string) => {
        override[prop] = dataItem?.[prop] ?? {}
        return override
    }, {})
}

export interface OverrideOrDefaultData {
    type: string
    prop: string
    value: any
    metaData: any
    whereToWriteInDM: string
    path?: string[]
}

const getAppComponents = (appData: any) => appData.components ?? []

const builderAppTypes = new Set(['STUDIO_WIDGET', 'EDITOR_REACT_COMPONENT'])
const isNewBuilderComponent = (compData: any) =>
    builderAppTypes.has(compData.type) && !isEmpty(compData.data?.componentModel)

const extractBuilderCompsFromAppData = (
    serviceTopology: STExtApi['serviceTopology'],
    appData: any
): {componentType: string; manifestUrl: string}[] => {
    const comps = getAppComponents(appData).filter(isNewBuilderComponent)
    return comps.map((comp: any) => {
        const {componentType} = comp.data.componentModel
        const urlKey = `${comp.componentId}_manifestUrl`
        const url = appData.appFields.platform.baseUrls[urlKey]
        const dataFixerVersion = serviceTopology.getServiceTopology().scriptsVersionsMap?.['@wix/santa-data-fixer']
        const manifestGeneratorVersion =
            serviceTopology.getServiceTopology().scriptsVersionsMap?.['blocks-builder-manifest-generator']
        const manifestUrl = url
            .replace('$MANIFEST_MODULE_VERSION$', manifestGeneratorVersion)
            .replace('$DATA_FIXER_VERSION$', dataFixerVersion)
        return {componentType, manifestUrl}
    })
}

/* Cast the Schema type to a Record<string, SchemaFile>
 * On the builder side of things, we want Schemas to remain opaque. Which is why they're defined as unknown.
 * Unfortunately, that requires a type conversion here to be able to use them in the schemaAPI.registerComponentDefinitionAndSchemas method.
 */
function asSchemas(data: Schema): Record<string, SchemaFile> {
    return data as Record<string, SchemaFile>
}

const registerBuilderComponent = (schemaAPI: SchemaAPI, details: BuilderCompDetails): void => {
    const {definition, schemas, identifier} = details
    const allowOverrides = true
    schemaAPI.registerComponentDefinitionAndSchemas(
        identifier,
        {
            componentDefinition: {
                [identifier]: definition
            },
            dataSchemas: asSchemas(schemas.data),
            propertiesSchemas: {},
            otherSchemas: {
                innerElements: asSchemas(schemas.innerElements),
                innerElementsMetaData: asSchemas(schemas.innerElementsMetaData),
                style: asSchemas(schemas.style),
                multilingualTranslations: asSchemas(schemas.multilingualTranslations),
                presets: asSchemas(schemas.presets),
                slots: asSchemas(schemas.slots)
            }
        },
        allowOverrides
    )
}

async function fetchBuilderCompDetails(
    fetchFn: FetchFn,
    identifier: string,
    manifestUrl: string,
    experimentInstance: Experiment
): Promise<BuilderCompDetails[]> {
    const response = await fetchFn(manifestUrl, {
        headers: {
            'x-wix-extended-timeout': '1'
        }
    })
    const manifest = await response.json()
    return deriveFromManifest(identifier, manifest, experimentInstance)
}

// @ts-expect-error - Unused until builder orchestration is implemented, replaced by editor-platform calling the setManifests method
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function initialize(dmApis: DmApis): Promise<void> {
    const {rendererModel, builderComponents} = dmApis.extensionAPI as RMApi & BuilderComponentsExtensionAPI & STExtApi
    const csm = rendererModel.getClientSpecMap()
    await Promise.all(
        Object.values(csm ?? {}).map(appData => builderComponents.registerBuilderComponentsForApp(appData))
    )
}

const createExtension = ({environmentContext}: CreateExtensionArgument): Extension => {
    return {
        name: 'builderComponents',
        dependencies: new Set(['rendererModel', 'innerElements', 'serviceTopology']),
        createPublicAPI(args: CreateExtArgs): BuilderComponentsPublicExtensionAPI {
            const {builderComponents} = args.extensionAPI as BuilderComponentsExtensionAPI
            return {
                builderComponents: {
                    setManifests: builderComponents.setManifests
                }
            }
        },
        createExtensionAPI(args: CreateExtArgs): BuilderComponentsExtensionAPI {
            const {schemaAPI, serviceTopology} = args.extensionAPI as SchemaExtensionAPI & STExtApi
            const {platform} = args.extensionAPI as PlatformExtensionAPI
            const {components} = args.extensionAPI as StructureExtensionAPI
            const {variants} = args.extensionAPI as VariantsExtensionAPI
            const {pointers, dal} = args
            const {fetchFn} = environmentContext
            const {experimentInstance} = args.coreConfig

            const getComponentDetailsFromCSM = (componentType: string): any => {
                const builderCompData = platform.getBuilderCompAppData(componentType)
                if (!builderCompData) return undefined
                const {appDefinitionId, componentId} = builderCompData
                const appPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
                const appData = dal.get(appPointer)
                const {data} = appData.components.find((comp: any) => comp.componentId === componentId)
                return data
            }

            const getOverridesOrDefaultFromType = (
                manifestData: Record<string, any>,
                override: any,
                manifestMetaData: ManifestMetaData,
                types: string[] | undefined
            ) => {
                const overrideOrDefaultDataArr: OverrideOrDefaultData[] = []
                Object.entries(manifestData).forEach(([prop, meta]: [string, any]) => {
                    const {[manifestMetaData.typeNameInManifest]: type} = meta
                    if (!types || types.includes(type)) {
                        const {value, whereToWriteInDM} = typeToExtractDataFunction[manifestMetaData.dataType](
                            meta,
                            override,
                            prop
                        )
                        overrideOrDefaultDataArr.push({
                            prop,
                            value,
                            type,
                            whereToWriteInDM,
                            metaData: meta[type] ?? {}
                        })
                    }
                })
                return overrideOrDefaultDataArr
            }

            const getComponentOverridesOrDefaultFromType = (
                compPointer: Pointer,
                manifestMetaData: ManifestMetaData,
                withInnerElements: boolean = false,
                types?: string[]
            ): OverrideOrDefaultData[] => {
                const {innerElements} = args.extensionAPI as InnerElementsExtensionAPI
                const getOverridesOrDefaultFromInnerElements = (
                    componentPointer: Pointer,
                    elements: Record<string, any>,
                    relativePath: string[],
                    overrideOrDefaultDataArr: OverrideOrDefaultData[],
                    requestedTypes?: string[]
                ) => {
                    if (!elements) return overrideOrDefaultDataArr
                    Object.entries(elements).forEach(([elem, val]: [string, any]) => {
                        const path = [components.getComponentType(componentPointer), ...relativePath, elem]
                        const {[manifestMetaData.dataType]: innerElementData} = val.inlineElement
                        const extractFunction =
                            manifestMetaData.dataType === DATA_TYPES.theme
                                ? innerElements.getInnerElementStyle(componentPointer as CompRef, path)
                                : innerElements.getInnerElementData(componentPointer as CompRef, path)
                        const innerElementsOverride = getComponentDataValues(extractFunction, manifestMetaData.dataType)
                        overrideOrDefaultDataArr.push(
                            ...getOverridesOrDefaultFromType(
                                innerElementData,
                                innerElementsOverride,
                                manifestMetaData,
                                requestedTypes
                            ).map(context => ({...context, path}))
                        )
                        getOverridesOrDefaultFromInnerElements(
                            componentPointer,
                            val.inlineElement.elements,
                            [...relativePath, elem],
                            overrideOrDefaultDataArr,
                            requestedTypes
                        )
                    })
                    return overrideOrDefaultDataArr
                }
                const componentType = components.getComponentType(compPointer)
                const {editorElement} = getComponentDetailsFromCSM(componentType)
                const {[manifestMetaData.dataType]: manifestData, elements} = editorElement
                if (!manifestData) return []
                const item = variants.getComponentItemConsideringVariants(compPointer, manifestMetaData.dataType)
                const compOverrides = getComponentDataValues(item, manifestMetaData.dataType)
                const elementPropsArray = getOverridesOrDefaultFromType(
                    manifestData,
                    compOverrides,
                    manifestMetaData,
                    types
                )

                return withInnerElements
                    ? [
                          ...elementPropsArray,
                          ...getOverridesOrDefaultFromInnerElements(compPointer, elements, [], [], types)
                      ]
                    : elementPropsArray
            }

            return {
                builderComponents: {
                    setManifests(manifests: Record<string, Builder.ResolvedManifest>): void {
                        if (experimentInstance.isOpen('dm_schemaResourceLoader')) {
                            console.log('Manifests should only be set via schema resource loader')
                            return
                        }
                        for (const [identifier, manifest] of Object.entries(manifests)) {
                            const derived = deriveFromManifest(identifier, manifest, experimentInstance)
                            for (const compDetails of derived) {
                                registerBuilderComponent(schemaAPI, compDetails)
                            }
                        }
                        console.log('Builder components manifests set')
                    },
                    async registerBuilderComponentsForApp(appData: any): Promise<void> {
                        const comps = extractBuilderCompsFromAppData(serviceTopology, appData)
                        await Promise.all(
                            comps.map(async ({componentType, manifestUrl}) => {
                                const derived = await fetchBuilderCompDetails(
                                    fetchFn,
                                    componentType,
                                    manifestUrl,
                                    experimentInstance
                                )
                                for (const compDetails of derived) {
                                    registerBuilderComponent(schemaAPI, compDetails)
                                }
                            })
                        )
                    },
                    deriveFromManifest,
                    registerBuilderComponent(details: BuilderCompDetails) {
                        registerBuilderComponent(schemaAPI, details)
                    },
                    registerBuilderComponentWithManifest(identifier: string, manifest: Builder.ResolvedManifest) {
                        const derived = deriveFromManifest(identifier, manifest, experimentInstance)
                        for (const compDetails of derived) {
                            registerBuilderComponent(schemaAPI, compDetails)
                        }
                    },
                    getArchType(componentType: string) {
                        const {editorElement} = getComponentDetailsFromCSM(componentType) ?? {}
                        return editorElement.archetype
                    },
                    getDisplayName(componentType: string) {
                        const {displayName} = getComponentDetailsFromCSM(componentType)?.editorElement ?? {}
                        return displayName
                    },
                    getStyle(compPointer: Pointer, withInnerElements: boolean = false, types?: string[]) {
                        return getComponentOverridesOrDefaultFromType(
                            compPointer,
                            {typeNameInManifest: 'cssPropertyType', dataType: 'style'},
                            withInnerElements,
                            types
                        )
                    },
                    getData(compPointer: Pointer, withInnerElements: boolean = false, types?: string[]) {
                        return getComponentOverridesOrDefaultFromType(
                            compPointer,
                            {typeNameInManifest: 'dataType', dataType: 'data'},
                            withInnerElements,
                            types
                        )
                    },
                    getBuilderTypes(compPointer: CompRef): BuilderTypes {
                        const compType = getComponentType(dal, compPointer)
                        return builderTypes(schemaAPI, compType)
                    },
                    isBuilder(compType: string) {
                        return !!platform.getBuilderCompAppData(compType)
                    }
                }
            }
        }
    }
}

export {createExtension, BuilderComponentsApi, BuilderComponentsExtensionAPI}
